import { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Headphones,
  MessageCircle,
  Heart,
  Users,
  CheckCircle,
  Star,
  Zap,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Globe,
  FileText,
  AlertCircle,
  Smile,
  Sparkles,
  Twitter,
  Linkedin,
  Youtube,
} from 'lucide-react';
import { useThemeStore } from '../../../store/theme';

// CSS Keyframe animations
const keyframeStyles = `
  @keyframes float-gentle {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-15px) rotate(3deg); }
  }

  @keyframes pulse-cyan {
    0%, 100% { box-shadow: 0 0 20px rgba(6, 182, 212, 0.4); }
    50% { box-shadow: 0 0 40px rgba(6, 182, 212, 0.7); }
  }

  @keyframes orbit-support {
    from { transform: rotate(0deg) translateX(120px) rotate(0deg); }
    to { transform: rotate(360deg) translateX(120px) rotate(-360deg); }
  }

  @keyframes orbit-support-reverse {
    from { transform: rotate(360deg) translateX(100px) rotate(-360deg); }
    to { transform: rotate(0deg) translateX(100px) rotate(0deg); }
  }

  @keyframes gradient-flow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }

  @keyframes ripple {
    0% { transform: scale(0.8); opacity: 1; }
    100% { transform: scale(2); opacity: 0; }
  }

  @keyframes bounce-soft {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes ticket-float {
    0%, 100% { transform: translateY(0) rotate(-5deg); opacity: 0.6; }
    50% { transform: translateY(-20px) rotate(5deg); opacity: 1; }
  }

  @keyframes chat-bubble {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }

  @keyframes satisfaction-pulse {
    0%, 100% { transform: scale(1); opacity: 0.8; }
    50% { transform: scale(1.1); opacity: 1; }
  }

  .animate-float-gentle { animation: float-gentle 6s ease-in-out infinite; }
  .animate-pulse-cyan { animation: pulse-cyan 3s ease-in-out infinite; }
  .animate-orbit-support { animation: orbit-support 20s linear infinite; }
  .animate-orbit-support-reverse { animation: orbit-support-reverse 15s linear infinite; }
  .animate-gradient-flow { animation: gradient-flow 4s ease infinite; background-size: 200% 200%; }
  .animate-ripple { animation: ripple 2s ease-out infinite; }
  .animate-bounce-soft { animation: bounce-soft 3s ease-in-out infinite; }
  .animate-shimmer { animation: shimmer 3s linear infinite; background-size: 200% 100%; }
  .animate-ticket-float { animation: ticket-float 4s ease-in-out infinite; }
  .animate-chat-bubble { animation: chat-bubble 2s ease-in-out infinite; }
  .animate-satisfaction-pulse { animation: satisfaction-pulse 2.5s ease-in-out infinite; }
`;

// Helper function to render markdown-style bold text
const renderMessageContent = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
};

// Chat demo messages
const chatMessages = [
  { role: 'user', message: "I've been waiting 3 days for my order and still no tracking info!" },
  { role: 'assistant', message: "I completely understand your frustration, and I sincerely apologize for the delay. Let me look into this right away. I found your order #78432 - it was delayed due to warehouse processing, but I've expedited shipping at no extra cost. You'll receive tracking within 2 hours, and your package will arrive by tomorrow. I've also added a 15% discount code for your next order. Is there anything else I can help with?" },
  { role: 'user', message: "Wow, that's really helpful! Thank you so much!" },
  { role: 'assistant', message: "You're very welcome! I've also set up a notification so you'll get real-time updates. Your satisfaction is my top priority. Have a wonderful day! " },
];

// Testimonials
const testimonials = [
  {
    name: 'Rachel Kim',
    role: 'Customer Success Director',
    company: 'CloudSync',
    avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop&crop=face',
    quote: 'Nova reduced our average response time from 4 hours to under 5 minutes. Our customer satisfaction scores increased by 47% in just two months.',
    rating: 5,
  },
  {
    name: 'Marcus Thompson',
    role: 'Support Team Lead',
    company: 'TechFlow',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face',
    quote: "The empathy Nova shows is remarkable. Customers often can't tell they're chatting with AI. It handles escalations perfectly and knows when to involve humans.",
    rating: 5,
  },
  {
    name: 'Jennifer Walsh',
    role: 'Head of Operations',
    company: 'RetailMax',
    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face',
    quote: 'We went from 200 daily tickets to handling 2000+ without adding staff. Nova manages inquiries across email, chat, and social media seamlessly.',
    rating: 5,
  },
];

// Capabilities with unique images
const capabilities = [
  {
    icon: Globe,
    title: 'Multi-Channel Support',
    description: 'Seamlessly manage customer conversations across email, live chat, social media, and phone - all from one unified interface.',
    image: 'https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=400&fit=crop',
  },
  {
    icon: FileText,
    title: 'Smart Ticket Management',
    description: 'Automatically categorize, prioritize, and route tickets to the right team. Track resolution times and prevent any inquiry from falling through.',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600&h=400&fit=crop',
  },
  {
    icon: MessageCircle,
    title: 'FAQ & Knowledge Base',
    description: 'Build comprehensive self-service resources. Nova learns from resolved tickets to suggest new FAQ articles automatically.',
    image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&h=400&fit=crop',
  },
  {
    icon: Mail,
    title: 'Response Templates',
    description: 'Create personalized, on-brand response templates that maintain consistency while feeling authentic and human.',
    image: 'https://images.unsplash.com/photo-1596526131083-e8c633c948d2?w=600&h=400&fit=crop',
  },
  {
    icon: AlertCircle,
    title: 'Smart Escalation',
    description: 'Automatically detect complex issues and route them to specialized agents. Never leave a customer hanging on difficult problems.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop',
  },
  {
    icon: Smile,
    title: 'Sentiment Analysis',
    description: 'Understand customer emotions in real-time. Identify frustrated customers early and proactively turn negative experiences positive.',
    image: 'https://images.unsplash.com/photo-1531482615713-2afd69097998?w=600&h=400&fit=crop',
  },
];

// Discover more tasks with unique GIFs
const discoverTasks = [
  {
    title: 'Handle Customer Inquiries',
    description: 'Respond to questions with empathy and precision',
    gif: 'https://media.giphy.com/media/3oKIPnAiaMCws8nOsE/giphy.gif',
    prompt: 'Help me respond to a customer asking about our refund policy',
  },
  {
    title: 'Create Support Templates',
    description: 'Build consistent, professional response templates',
    gif: 'https://media.giphy.com/media/LmNwrBhejkK9EFP504/giphy.gif',
    prompt: 'Create a response template for handling shipping delays',
  },
  {
    title: 'Analyze Customer Feedback',
    description: 'Turn feedback into actionable insights',
    gif: 'https://media.giphy.com/media/3oKIPEqDGUULpEU0aQ/giphy.gif',
    prompt: 'Analyze these customer reviews and identify common pain points',
  },
  {
    title: 'Build FAQ Content',
    description: 'Create self-service knowledge base articles',
    gif: 'https://media.giphy.com/media/l46Cy1rHbQ92uuLXa/giphy.gif',
    prompt: 'Help me write an FAQ article about our product warranty',
  },
  {
    title: 'Handle Escalations',
    description: 'Navigate complex customer situations with care',
    gif: 'https://media.giphy.com/media/xT9C25UNTwfZuk85WP/giphy.gif',
    prompt: 'A customer is threatening to leave a bad review, help me de-escalate',
  },
  {
    title: 'Generate Support Reports',
    description: 'Track metrics and identify improvement areas',
    gif: 'https://media.giphy.com/media/3oKIPtjElfqwMOTbH2/giphy.gif',
    prompt: 'Create a weekly support performance report template',
  },
];

// Related employees
const relatedEmployees = [
  { id: 'agent', name: 'Agent', role: 'Conversational Agent', avatar: '🤖', gradient: 'from-sky-500 to-blue-500' },
  { id: 'aria', name: 'Aria', role: 'Social Media Manager', avatar: '🎀', gradient: 'from-pink-500 to-rose-500' },
  { id: 'echo', name: 'Echo', role: 'Email Marketing', avatar: '📧', gradient: 'from-orange-500 to-amber-500' },
];

// Stats
const stats = [
  { label: 'Faster Response', value: '95%', icon: Zap },
  { label: 'Customer Satisfaction', value: '4.9/5', icon: Star },
  { label: 'Tickets Resolved', value: '10M+', icon: CheckCircle },
  { label: 'Languages Supported', value: '50+', icon: Globe },
];

export default function NovaEmployee() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const chatRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(chatRef, { once: true, margin: '-100px' });

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate chat messages when in view
  useEffect(() => {
    if (isInView && visibleMessages < chatMessages.length) {
      const timeout = setTimeout(() => {
        setVisibleMessages((prev) => prev + 1);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isInView, visibleMessages]);

  // JSON-LD Schema
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Nova - AI Customer Support Assistant',
    applicationCategory: 'BusinessApplication',
    description: 'AI-powered customer support assistant that delivers exceptional experiences with empathy, efficiency, and expertise in resolving any inquiry.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '3847',
    },
    operatingSystem: 'Web-based',
    featureList: [
      'Multi-channel support',
      'Smart ticket management',
      'FAQ and knowledge base creation',
      'Response templates',
      'Intelligent escalation handling',
      'Customer sentiment analysis',
      '24/7 availability',
      '50+ language support',
    ],
  };

  return (
    <>
      <Helmet>
        <title>Nova - AI Customer Support Assistant | SweetPilot</title>
        <meta
          name="description"
          content="Meet Nova, your AI customer support specialist. Deliver exceptional customer experiences with empathy, efficiency, and 24/7 availability across all channels."
        />
        <meta
          name="keywords"
          content="AI customer support, customer service AI, helpdesk automation, ticket management, FAQ builder, sentiment analysis, multi-channel support"
        />
        <link rel="canonical" href="https://sweetpilot.com/employees/nova" />

        {/* Open Graph */}
        <meta property="og:title" content="Nova - AI Customer Support Assistant | SweetPilot" />
        <meta
          property="og:description"
          content="Deliver exceptional customer experiences with Nova. AI-powered support that's empathetic, efficient, and available 24/7."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sweetpilot.com/employees/nova" />
        <meta property="og:image" content="https://sweetpilot.com/og-nova.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Nova - AI Customer Support Assistant" />
        <meta
          name="twitter:description"
          content="Deliver exceptional customer experiences with Nova. AI-powered support that's empathetic, efficient, and available 24/7."
        />
        <meta name="twitter:image" content="https://sweetpilot.com/og-nova.png" />

        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{keyframeStyles}</style>

      <div className={`min-h-screen ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
        {/* Floating Support Tickets Background */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-ticket-float"
              style={{
                left: `${10 + i * 12}%`,
                top: `${20 + (i % 3) * 25}%`,
                animationDelay: `${i * 0.6}s`,
                animationDuration: `${4 + i * 0.5}s`,
              }}
            >
              <Headphones
                className={`w-6 h-6 ${isDark ? 'text-cyan-500/20' : 'text-cyan-400/30'}`}
              />
            </div>
          ))}
        </div>

        {/* Navigation Header */}
        <nav className={`fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-dark-900/80' : 'bg-white/80'} backdrop-blur-xl border-b ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/employees" className={`flex items-center gap-2 ${isDark ? 'text-gray-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'} transition-colors`}>
                <ChevronLeft className="w-5 h-5" />
                <span>Back to Employees</span>
              </Link>
              <Link
                to="/dashboard/employees/nova"
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-cyan-500/25 transition-all"
              >
                Chat with Nova
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          {/* Background gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-teal-500/10" />

          {/* Animated ripples */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
            <div className="w-96 h-96 rounded-full border-2 border-cyan-500/20 animate-ripple" />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-cyan-500/20 animate-ripple" style={{ animationDelay: '0.5s' }} />
            <div className="absolute inset-0 w-96 h-96 rounded-full border-2 border-cyan-500/20 animate-ripple" style={{ animationDelay: '1s' }} />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* Breadcrumbs */}
            <nav className="mb-8">
              <ol className={`flex items-center gap-2 text-sm ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
                <li>
                  <Link
                    to="/employees"
                    className={`transition-colors ${isDark ? 'hover:text-cyan-400' : 'hover:text-cyan-600'}`}
                  >
                    AI Employees
                  </Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className={isDark ? 'text-white' : 'text-slate-900'}>
                  Nova
                </li>
              </ol>
            </nav>

            <div className="text-center">
              {/* Avatar with orbiting icons */}
              <div className="relative w-48 h-48 mx-auto mb-8">
                {/* Orbiting icons */}
                <div className="absolute inset-0 animate-orbit-support">
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-cyan-500/20' : 'bg-cyan-100'}`}>
                      <Headphones className="w-6 h-6 text-cyan-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-support-reverse">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-teal-500/20' : 'bg-teal-100'}`}>
                      <MessageCircle className="w-6 h-6 text-teal-500" />
                    </div>
                  </div>
                </div>
                <div className="absolute inset-0 animate-orbit-support" style={{ animationDelay: '-10s' }}>
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-100'}`}>
                      <Heart className="w-6 h-6 text-emerald-500" />
                    </div>
                  </div>
                </div>

                {/* Main avatar */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', duration: 0.8 }}
                  className="absolute inset-0 flex items-center justify-center"
                >
                  <div className="w-36 h-36 rounded-3xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center animate-float-gentle animate-pulse-cyan">
                    <span className="text-7xl">💙</span>
                  </div>
                </motion.div>
              </div>

              {/* Title */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h1 className={`text-5xl md:text-7xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Meet{' '}
                  <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
                    Nova
                  </span>
                </h1>
                <p className={`text-xl md:text-2xl mb-2 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                  Your AI Customer Support Specialist
                </p>
                <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                  Delivering exceptional customer experiences with empathy, efficiency, and expertise in resolving any inquiry - 24 hours a day, 7 days a week.
                </p>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex flex-wrap justify-center gap-4 mt-8"
              >
                <Link
                  to="/dashboard/employees/nova"
                  className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-cyan-500/30 transition-all flex items-center gap-2"
                >
                  Start Supporting Customers
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="#capabilities"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10'
                      : 'border-cyan-500 text-cyan-600 hover:bg-cyan-50'
                  }`}
                >
                  Explore Features
                </a>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 max-w-4xl mx-auto"
              >
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-2xl ${isDark ? 'bg-white/5' : 'bg-white shadow-sm'}`}
                  >
                    <stat.icon className={`w-6 h-6 mx-auto mb-2 ${isDark ? 'text-cyan-400' : 'text-cyan-500'}`} />
                    <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {stat.value}
                    </div>
                    <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Chat Demo Section */}
        <section ref={chatRef} className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                See Nova in Action
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Watch how Nova transforms frustrated customers into loyal advocates
              </p>
            </motion.div>

            {/* Chat Window */}
            <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-dark-900 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              {/* Chat Header */}
              <div className={`px-6 py-4 border-b ${isDark ? 'bg-dark-800 border-white/10' : 'bg-white border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center">
                    <span className="text-lg">💙</span>
                  </div>
                  <div>
                    <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Nova</div>
                    <div className="flex items-center gap-1 text-sm text-emerald-500">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                      Online - Avg. response: 8 seconds
                    </div>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div className="p-6 space-y-4 min-h-[400px]">
                <AnimatePresence>
                  {chatMessages.slice(0, visibleMessages).map((msg, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ duration: 0.3 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-4 rounded-2xl ${
                          msg.role === 'user'
                            ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white'
                            : isDark
                            ? 'bg-dark-700 text-gray-200'
                            : 'bg-white text-slate-700 shadow-sm'
                        }`}
                      >
                        <span className="whitespace-pre-line">{renderMessageContent(msg.message)}</span>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>

                {/* Typing indicator */}
                {isInView && visibleMessages < chatMessages.length && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className={`px-4 py-3 rounded-2xl ${isDark ? 'bg-dark-700' : 'bg-white shadow-sm'}`}>
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Section */}
        <section id="capabilities" className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Everything You Need for{' '}
                <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">
                  Outstanding Support
                </span>
              </h2>
              <p className={`text-lg max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Nova comes equipped with powerful features to handle any customer inquiry with care and efficiency
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`group rounded-2xl overflow-hidden border ${
                    isDark ? 'bg-dark-800/50 border-white/5 hover:border-cyan-500/30' : 'bg-white border-slate-200 hover:border-cyan-300'
                  } transition-all hover:shadow-xl hover:shadow-cyan-500/10`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={capability.image}
                      alt={capability.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="p-2 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500">
                        <capability.icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {capability.title}
                    </h3>
                    <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                      {capability.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Support Channels Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Support Across Every Channel
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Nova integrates with all your customer touchpoints for unified support
              </p>
            </motion.div>

            <div className="flex flex-wrap justify-center gap-6">
              {[
                { icon: Mail, label: 'Email', color: 'from-blue-500 to-cyan-500' },
                { icon: MessageCircle, label: 'Live Chat', color: 'from-cyan-500 to-teal-500' },
                { icon: Phone, label: 'Phone', color: 'from-teal-500 to-emerald-500' },
                { icon: Users, label: 'Social Media', color: 'from-emerald-500 to-green-500' },
                { icon: Globe, label: 'Website', color: 'from-green-500 to-cyan-500' },
              ].map((channel, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col items-center gap-3 p-6 rounded-2xl ${
                    isDark ? 'bg-dark-900/50' : 'bg-slate-50'
                  }`}
                >
                  <div className={`p-4 rounded-2xl bg-gradient-to-r ${channel.color}`}>
                    <channel.icon className="w-8 h-8 text-white" />
                  </div>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {channel.label}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Discover More Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Discover What Nova Can Do
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Click any task to try it with Nova
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {discoverTasks.map((task, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/dashboard/employees/nova?prompt=${encodeURIComponent(task.prompt)}`}
                    className={`group relative block h-64 rounded-2xl overflow-hidden border ${
                      isDark ? 'border-white/10 hover:border-cyan-500/50' : 'border-slate-200 hover:border-cyan-300'
                    } transition-all`}
                  >
                    {/* Background Image/GIF */}
                    <div className="absolute inset-0">
                      <img
                        src={task.gif}
                        alt={task.title}
                        className="w-full h-full object-cover opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                      />
                      <div className={`absolute inset-0 ${isDark ? 'bg-dark-800' : 'bg-white'} group-hover:opacity-80 transition-opacity`} />
                    </div>

                    {/* Content */}
                    <div className="relative h-full p-6 flex flex-col justify-end">
                      <div className="mb-auto">
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <Headphones className="w-6 h-6 text-white" />
                        </div>
                      </div>
                      <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {task.title}
                      </h3>
                      <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {task.description}
                      </p>
                      <div className={`mt-4 flex items-center gap-2 text-cyan-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity`}>
                        <span>Try this now</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className={`py-20 ${isDark ? 'bg-dark-800/50' : 'bg-white'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Trusted by Support Teams
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                See how Nova is transforming customer support
              </p>
            </motion.div>

            {/* Testimonial Carousel */}
            <div className="relative">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentTestimonial}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.3 }}
                  className={`p-8 rounded-2xl ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}
                >
                  {/* Stars */}
                  <div className="flex gap-1 mb-4 justify-center">
                    {[...Array(testimonials[currentTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ))}
                  </div>

                  {/* Quote */}
                  <p className={`text-xl text-center mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                    "{testimonials[currentTestimonial].quote}"
                  </p>

                  {/* Author */}
                  <div className="flex items-center justify-center gap-4">
                    <img
                      src={testimonials[currentTestimonial].avatar}
                      alt={testimonials[currentTestimonial].name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="text-left">
                      <div className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {testimonials[currentTestimonial].name}
                      </div>
                      <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                        {testimonials[currentTestimonial].role} at {testimonials[currentTestimonial].company}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentTestimonial
                        ? 'w-8 bg-gradient-to-r from-cyan-500 to-teal-500'
                        : isDark
                        ? 'bg-white/20 hover:bg-white/40'
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Related Employees */}
        <section className={`py-20 ${isDark ? 'bg-dark-900' : 'bg-slate-50'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Meet More AI Employees
              </h2>
              <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Build your complete AI team
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedEmployees.map((employee, index) => (
                <motion.div
                  key={employee.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/employees/${employee.id}`}
                    className={`group block p-6 rounded-2xl border transition-all ${
                      isDark
                        ? 'bg-dark-800/50 border-white/5 hover:border-white/20'
                        : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${employee.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <span className="text-3xl">{employee.avatar}</span>
                    </div>
                    <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {employee.name}
                    </h3>
                    <p className={`${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {employee.role}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={`py-20 ${isDark ? 'bg-gradient-to-br from-cyan-500/10 via-dark-900 to-teal-500/10' : 'bg-gradient-to-br from-cyan-50 via-white to-teal-50'}`}>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center animate-float-gentle">
                <span className="text-5xl">💙</span>
              </div>
              <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Ready to Transform Your Customer Support?
              </h2>
              <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                Start delivering exceptional customer experiences today with Nova
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/dashboard/employees/nova"
                  className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-xl text-white font-semibold text-lg hover:shadow-2xl hover:shadow-cyan-500/30 transition-all flex items-center gap-2"
                >
                  Chat with Nova Now
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/employees"
                  className={`px-8 py-4 rounded-xl font-semibold text-lg border-2 transition-all ${
                    isDark
                      ? 'border-white/20 text-white hover:bg-white/5'
                      : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  View All Employees
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className={`py-16 border-t ${isDark ? 'border-white/5 bg-dark-900' : 'border-slate-200 bg-white'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
              {/* Logo */}
              <div className="col-span-2">
                <Link to="/" className="inline-block mb-4">
                  <img
                    src={isDark ? '/logos/logo-5.png' : '/logos/logo-6.png'}
                    alt="SweetPilot"
                    className="w-48 object-contain"
                  />
                </Link>
                <p className={`text-sm mb-4 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                  AI employees that never sleep. Build, grow, and scale your
                  business 24/7.
                </p>
                <div className="flex space-x-3">
                  {[
                    { name: 'Twitter', icon: Twitter, href: 'https://twitter.com/flowai' },
                    { name: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/company/flowai' },
                    { name: 'Discord', icon: MessageCircle, href: 'https://discord.gg/flowai' },
                    { name: 'YouTube', icon: Youtube, href: 'https://youtube.com/@flowai' },
                  ].map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        isDark
                          ? 'bg-white/5 text-gray-400 hover:text-white hover:bg-white/10'
                          : 'bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200'
                      }`}
                    >
                      <span className="sr-only">{social.name}</span>
                      <social.icon className="w-5 h-5" />
                    </a>
                  ))}
                </div>
              </div>

              {/* Links */}
              {[
                {
                  title: 'Product',
                  links: [
                    { name: 'AI Employees', href: '/employees' },
                    { name: 'Features', href: '/features' },
                    { name: 'Pricing', href: '/pricing' },
                    { name: 'Integrations', href: '/integrations' },
                    { name: 'API', href: '/api' },
                  ],
                },
                {
                  title: 'Company',
                  links: [
                    { name: 'About', href: '/about' },
                    { name: 'Blog', href: '/blog' },
                    { name: 'Careers', href: '/careers' },
                    { name: 'Press', href: '/press' },
                    { name: 'Contact', href: '/contact' },
                  ],
                },
                {
                  title: 'Resources',
                  links: [
                    { name: 'Documentation', href: '/docs' },
                    { name: 'Help Center', href: '/help' },
                    { name: 'Community', href: '/community' },
                    { name: 'Templates', href: '/templates' },
                    { name: 'Roadmap', href: '/roadmap' },
                    { name: 'Status', href: '/status' },
                  ],
                },
                {
                  title: 'Legal',
                  links: [
                    { name: 'Privacy', href: '/privacy' },
                    { name: 'Terms', href: '/terms' },
                    { name: 'Security', href: '/security' },
                    { name: 'Cookies', href: '/cookies' },
                    { name: 'GDPR', href: '/gdpr' },
                    { name: 'Refund Policy', href: '/refund-policy' },
                  ],
                },
              ].map((column) => (
                <div key={column.title}>
                  <h4 className={`font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>{column.title}</h4>
                  <ul className="space-y-3">
                    {column.links.map((link) => (
                      <li key={link.name}>
                        <Link
                          to={link.href}
                          className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className={`flex flex-col md:flex-row items-center justify-between pt-8 border-t ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
              <p className={`text-sm mb-4 md:mb-0 ${isDark ? 'text-gray-500' : 'text-slate-500'}`}>
                &copy; {new Date().getFullYear()} SweetPilot. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <Link to="/privacy" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Privacy Policy
                </Link>
                <Link to="/terms" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Terms of Service
                </Link>
                <Link to="/cookies" className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}>
                  Cookies
                </Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
